/**
 * Where everything stands. Alignment-critical placements (torii, the aligned island, the portrait camera) are laid
 * on the torii azimuth ray from the landscape camera EYE, so the equinox sun and the island peak sit in the torii
 * opening as seen from the camera, not from the platform centre (6° of parallax otherwise).
 */
import type { AssetName } from '../assets/catalog';
import { WORLD } from '../config/world';
import { azAltToDirection, type Vec3 } from './frame';

export interface Placement {
  asset: AssetName;
  position: Vec3;
  rotationYDeg: number;
  /** Radius (m) of the ground footprint around `position`, for overlap checks. */
  footprintM: number;
}

/** Landscape camera position (eye height 1.6 m, 6 m behind the platform centre). */
export const EYE: Vec3 = [0, 1.6, 6];
export const SEA_LEVEL_Y = -80;
export const TORII_DISTANCE_M = 19;
/** Distance from the eye to the terrace edge in the view direction; beyond it the hill drops toward the town. */
export const PLATFORM_EDGE_M = 9;
/** The hill meets the sea here (just past the town). */
export const SHORE_M = 1_560;

/** Terrain height below the terrace: steep just past the edge, flattening toward the shore (SHORE_M). */
export function groundY(distM: number): number {
  if (distM <= PLATFORM_EDGE_M) return -0.3;
  const t = Math.min(1, (distM - PLATFORM_EDGE_M) / (SHORE_M - PLATFORM_EDGE_M));
  return (SEA_LEVEL_Y + 1.5) * Math.pow(t, 0.5) - 0.3 * (1 - t);
}

/**
 * Terrace outline (world x, z) — the world pass (spec 2026-09-27-world-pass-design, site.ts). Front edge ~9 m ahead;
 * a jut on the right carries the torii and ends ~1 m behind it at the stairs head, 0.38 m outside the stair rails
 * (STAIRS_HEAD_CORNERS); its right edge runs back toward the viewer, out of the landscape frame's bottom-right corner.
 * On the left it carries the whole haiden (its seaward face 0.3 m from the edge) and the honden reserve behind it.
 */
export const PLATFORM_OUTLINE: Array<[number, number]> = [
  [-16, 10], [-16.2, -9.6], [-12.1, -13.8], [-8.3, -10.0], [-7.83, -7.65], [-3.59, -3.41], [1.2, -3.0], [0.8, -8],
  [0.94, -14.31], [8.06, -12.67], [3.9, 4.0], [3.9, 10],
];

/** Horizontal distance from (x, z) to the terrace (0 inside PLATFORM_OUTLINE). */
export function outlineDistance(x: number, z: number): number {
  let inside = false;
  let best = Infinity;
  const n = PLATFORM_OUTLINE.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [ax, az] = PLATFORM_OUTLINE[j]!;
    const [bx, bz] = PLATFORM_OUTLINE[i]!;
    if (bz > z !== az > z && x < ((ax - bx) * (z - bz)) / (az - bz) + bx) inside = !inside;
    const lx = ax - bx;
    const lz = az - bz;
    const t = Math.min(1, Math.max(0, ((x - bx) * lx + (z - bz) * lz) / (lx * lx + lz * lz)));
    best = Math.min(best, Math.hypot(x - bx - t * lx, z - bz - t * lz));
  }
  return inside ? 0 : best;
}

/** Point at a horizontal distance along a compass azimuth from `from`, at height y. */
export function alongAzimuth(azDeg: number, distM: number, y: number, from: Vec3 = EYE): Vec3 {
  const [dx, , dz] = azAltToDirection(azDeg, 0, WORLD.cameraAzimuthDeg);
  return [from[0] + dx * distM, y, from[2] + dz * distM];
}

/** three.js yaw so an object's local +Z faces from `at` toward `to` (horizontal). */
function yawToward(at: Vec3, to: Vec3): number {
  return (Math.atan2(to[0] - at[0], to[2] - at[2]) * 180) / Math.PI;
}

const TORII: Vec3 = alongAzimuth(WORLD.toriiAzimuthDeg, TORII_DISTANCE_M, 0);
const TORII_YAW = yawToward(TORII, EYE);
const yawRad = (TORII_YAW * Math.PI) / 180;
const TORII_X: Vec3 = [Math.cos(yawRad), 0, -Math.sin(yawRad)]; // torii local +X in world
const INNER_HALF_SPAN_M = 1.85;
const NUKI_UNDERSIDE_M = 3.25;
const at = (base: Vec3, along: number, y: number): Vec3 => [base[0] + TORII_X[0] * along, y, base[2] + TORII_X[2] * along];

/** Pillar base centres (world), 2.1 m either side of the torii axis — where the fence closes like a gate. */
export const TORII_PILLARS = { left: at(TORII, -2.1, 0), right: at(TORII, 2.1, 0) };

export const TORII_OPENING = {
  bottomLeft: at(TORII, -INNER_HALF_SPAN_M, 0),
  bottomRight: at(TORII, INNER_HALF_SPAN_M, 0),
  topLeft: at(TORII, -INNER_HALF_SPAN_M, NUKI_UNDERSIDE_M),
};

export const ALIGNED_ISLAND_DISTANCE_M = 6_000;
export const ALIGNED_ISLAND_PEAK: Vec3 = alongAzimuth(WORLD.toriiAzimuthDeg, ALIGNED_ISLAND_DISTANCE_M, SEA_LEVEL_Y + 280);

/**
 * Stairs (built in ground/stairs.ts from site.ts): top edge 1 m behind the torii, descending away from the camera to
 * the lane; 5.6 m wide, so their wooden rails run outside the pillars and carry the terrace fence on down the slope.
 */
export const STAIRS_WIDTH_M = 5.6;
/** Rail centre line from the stairs axis (half width + 0.12): on the stone curb, outside the torii pillars. */
export const STAIR_RAIL_OFFSET_M = STAIRS_WIDTH_M / 2 + 0.12;
/** Half width of the stairs' stone body: treads, then a 0.75 m curb carrying the rails and, outside them, the lamps. */
export const STAIRS_BODY_HALF_M = STAIRS_WIDTH_M / 2 + 0.75;
export const STAIRS_TOP_ALONG_M = TORII_DISTANCE_M + 1.0;
/** Each stair rail starts with a post on the terrace, 0.15 m before the top step: the fence ends there. */
const RAIL_HEAD_CENTRE = alongAzimuth(WORLD.toriiAzimuthDeg, STAIRS_TOP_ALONG_M - 0.15, 0);
export const STAIR_RAIL_HEADS = {
  left: at(RAIL_HEAD_CENTRE, -STAIR_RAIL_OFFSET_M, 0),
  right: at(RAIL_HEAD_CENTRE, STAIR_RAIL_OFFSET_M, 0),
};
/** Stairs head corners on the terrace outline: 0.1 m outside the stairs' body. */
export const STAIRS_HEAD_CORNERS = {
  left: at(alongAzimuth(WORLD.toriiAzimuthDeg, STAIRS_TOP_ALONG_M, 0), -(STAIRS_BODY_HALF_M + 0.1), 0),
  right: at(alongAzimuth(WORLD.toriiAzimuthDeg, STAIRS_TOP_ALONG_M, 0), STAIRS_BODY_HALF_M + 0.1, 0),
};

export const COMPOSITION: Record<string, Placement> = {
  torii: { asset: 'torii', position: TORII, rotationYDeg: TORII_YAW, footprintM: 2.3 },
  haiden_edge: { asset: 'haiden_edge', position: [-3.8, 0, -3.2], rotationYDeg: 45, footprintM: 2.0 },
  toro: { asset: 'toro', position: [-1.2, 0, -2.2], rotationYDeg: 0, footprintM: 0.5 },
  sakura_v0: { asset: 'sakura_v0', position: [-5.6, 0, 1.2], rotationYDeg: 70, footprintM: 0.6 },
};
